import type {ShareView} from './types'

/**
 * Whether a folder in My Files can be shared, before asking the server (E5, E10). The server makes
 * the same checks; this only lets the menu say why the item is disabled.
 */
export type ShareEligibility =
  /** The folder is already shared: open its sharing settings. */
  | {kind: 'shared'; share: ShareView}
  | {kind: 'available'}
  | {kind: 'blocked'; reason: ShareBlockedReason; message: string}

export type ShareBlockedReason =
  | 'root'
  | 'inside_shared'
  | 'contains_shared'
  | 'unsupported_character'

/** Same set as `share_authz._UNSUPPORTED_CHARS`, plus control characters. */
// eslint-disable-next-line no-control-regex
const UNSUPPORTED = /[*?$"'\\\u0000-\u001f\u007f-\u009f]/

const folderName = (folderPath: string): string =>
  folderPath.replace(/\/$/, '').split('/').pop() || folderPath

/** `folderPath` is relative to My Files and ends with `/`; `''` is the root. */
export function shareEligibility(folderPath: string, sharedByMe: ShareView[]): ShareEligibility {
  if (folderPath === '' || folderPath === '/') {
    return {
      kind: 'blocked',
      reason: 'root',
      message: "The root of My Files can't be shared. Share a folder inside it.",
    }
  }
  const exact = sharedByMe.find((s) => s.folderPath === folderPath)
  if (exact) {
    return {kind: 'shared', share: exact}
  }
  const above = sharedByMe.find((s) => folderPath.startsWith(s.folderPath))
  if (above) {
    return {
      kind: 'blocked',
      reason: 'inside_shared',
      message: `It's inside the shared folder "${above.name || folderName(above.folderPath)}".`,
    }
  }
  const inside = sharedByMe.find((s) => s.folderPath.startsWith(folderPath))
  if (inside) {
    return {
      kind: 'blocked',
      reason: 'contains_shared',
      message: `It contains the shared folder "${inside.name || folderName(inside.folderPath)}".`,
    }
  }
  if (UNSUPPORTED.test(folderPath)) {
    return {
      kind: 'blocked',
      reason: 'unsupported_character',
      message: 'Its path contains * ? $ quotes or backslashes. Rename it to share it.',
    }
  }
  return {kind: 'available'}
}

/** The last segment of a folder path, for titles. */
export function shareFolderDisplayName(folderPath: string): string {
  return folderName(folderPath)
}
